<template>
  <div class="total-price-block__wrapper">
    <quote-remark />

    <div v-if="!showInclPrice" class="total-price-block__taxes-incl">
      <ul>
        <li>
          <span>Excl. BTW/BPM</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(totalPriceExcl, 'currency') }}
          </span>
        </li>
        <li>
          <span>BTW</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(amountDue?.Btw, 'currency') }}
          </span>
        </li>
        <li>
          <span>BPM-bedrag</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(amountDue?.BPM, 'currency') }}
          </span>
        </li>
      </ul>
    </div>

    <div class="total-price-block__price">
      <strong>{{ i18n.t('/TotalPrice/Total Price Label') }}</strong>
      <div>
        <span v-if="showExclPrice && showInclPrice" data-qa="total-price-excl">
          {{ i18n.n(totalPriceExcl, 'currency') }}
        </span>
        <span class="total-price-block__total-price" data-qa="total-price-incl">
          <strong>
            {{ i18n.n(totalPriceIncl, 'currency') }}
            <a v-if="isEditable" @click="toggleModal(true)">
              <icon icon="pencil" />
            </a>
          </strong>
        </span>
      </div>
    </div>

    <div v-if="!showExclPrice" class="total-price-block__taxes-excl">
      <ul>
        <li>
          <span>Excl. BTW/BPM</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(totalPriceExcl, 'currency') }}
          </span>
        </li>
        <li>
          <span>BTW</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(amountDue?.Btw, 'currency') }}
          </span>
        </li>
        <li>
          <span>BPM-bedrag</span>
          <span data-qa="total-price-excl-btw-bpm">
            {{ i18n.n(amountDue?.BPM, 'currency') }}
          </span>
        </li>
      </ul>
    </div>

    <div class="total-price-block__total-price-excl">
      <span>Bedrijfsinvestering (totaal excl. BTW en incl. BPM)</span>
      <span data-qa="bedrijfsinvestering-total-price-excl">
        {{ i18n.n(totalPriceExclBtwInclBpm, 'currency') }}
      </span>
      <span></span>
    </div>

    <modal v-if="showModal" :title="i18n.t('/TotalPrice/Edit/Title')" @close="toggleModal(false)">
      <dl class="price-details">
        <dt>{{ i18n.t('/TotalPrice/Edit/Excluding Vat Price') }}</dt>
        <dd>{{ i18n.n(totalPriceExcl, 'currency') }}</dd>
        <dt>
          <strong>{{ i18n.t('/TotalPrice/Edit/Including Vat Price') }}</strong>
        </dt>
        <dd>
          <strong>{{ i18n.n(totalPriceIncl, 'currency') }}</strong>
        </dd>
      </dl>

      <form @submit.prevent="submit">
        <app-fieldset :title="i18n.t('/TotalPrice/Edit/Edit Total Price')" class="price-form">
          <price-input
            id="desiredPrice"
            v-model="desiredPriceInclBtw"
            v-autofocus
            name="desiredPrice"
            :title="i18n.t('/TotalPrice/Edit/Desired Price Label')"
          />
        </app-fieldset>

        <app-button data-qa="edit-total-price-button" button-type="submit">
          {{ i18n.t('/TotalPrice/Edit/Edit Button Label') }}
        </app-button>
      </form>
    </modal>

    <financial-overview-modal v-if="showFinancialOverviewButton" />
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import Icon from '@/components/generic/presentational/atoms/icon/Icon.vue'
import AppFieldset from '@/components/generic/presentational/atoms/appFieldset/AppFieldset.vue'
import PriceInput from '@/components/generic/presentational/atoms/priceInput/PriceInput.vue'
import constants from '@/utils/constants'
import autoFocus from '@/mixins/autoFocus.js'
import { mapGetters } from 'vuex'
import FinancialOverviewModal from '@/components/generic/presentational/organisms/financialOverview/FinancialOverviewModal.vue'
import QuoteRemark from '@/components/generic/presentational/molecules/quoteRemark/QuoteRemark.vue'

export default {
  components: {
    Modal,
    Icon,
    AppButton,
    AppFieldset,
    PriceInput,
    FinancialOverviewModal,
    QuoteRemark,
  },
  mixins: [autoFocus],
  props: {
    showFinancialOverviewButton: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      desiredPriceInclBtw: 0,
      showModal: false,
      correctionCategoryCode: constants.CORRECTION_CATEGORY_CODE,
    }
  },
  computed: {
    ...mapGetters({
      totalPriceExcl: 'quote/totalPriceExclBtw',
      totalPriceIncl: 'quote/totalPriceInclBtw',
      totalPriceExclBtwInclBpm: 'quote/totalPriceExclBtwInclBpm',
      showInclPrice: 'quote/isPriceShowIncl',
      showExclPrice: 'quote/isPriceShowExcl',
      amountDue: 'quote/amountDue',
    }),
    isEditable() {
      return !this.$store.getters['quote/quoteStatusIsSalesOrder']
    },
  },
  methods: {
    toggleModal(show) {
      if (show) {
        this.desiredPriceInclBtw = this.totalPriceIncl
        this.showModal = true
      } else {
        this.showModal = false
      }
    },
    calculateCorrection(totalPrice, desiredPrice, currentCorrectionPrice) {
      // there is an existing correction
      const totalPriceWithoutCorrection = totalPrice + currentCorrectionPrice * -1
      return (totalPriceWithoutCorrection - desiredPrice).toFixed(2)
    },
    async setTotalPriceCorrection() {
      let correctionAmount = 0
      const currentCorrectionItem = this.$store.getters['quote/customCorrectionProduct'](
        this.correctionCategoryCode
      )

      if (currentCorrectionItem) {
        correctionAmount = this.calculateCorrection(
          this.totalPriceIncl,
          this.desiredPriceInclBtw,
          currentCorrectionItem.Price.Incl
        )
      } else {
        correctionAmount = this.calculateCorrection(
          this.totalPriceIncl,
          this.desiredPriceInclBtw,
          0
        )
      }

      let updatedCorrection = Object.assign({}, currentCorrectionItem)

      if (currentCorrectionItem) {
        await this.$store.dispatch('quote/removeFromAccessories', currentCorrectionItem)
      }

      updatedCorrection = {
        Name: 'Correctie',
        Price: {
          Incl: correctionAmount * -1,
        },
        Standard: false,
        AnalysisCode: this.correctionCategoryCode,
      }

      await this.$store.dispatch('quote/modifyOrAddAccessory', updatedCorrection)
    },

    async submit() {
      await this.setTotalPriceCorrection()
      this.toggleModal(false)
    },
  },
}
</script>

<style lang="scss" scoped>
.total-price-block__wrapper {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.total-price-block__price {
  display: flex;
  justify-content: space-between;
  margin: 1em 0;
  font-size: 1.4em;
}

.total-price-block__total-price {
  margin: 0 0 0 2.5em;
}

.price-details {
  display: grid;
  grid-template-columns: 1fr 2fr;
  margin-bottom: 1em;
}

.price-form {
  width: 20em;
}

.total-price-block__total-price-excl {
  display: flex;

  span {
    flex-basis: 20%;
    text-align: right;

    &:first-child {
      flex-basis: 60%;
      text-align: left;
    }
  }
}

.total-price-block__taxes-excl {
  width: 80%;
}

.total-price-block__taxes-incl {
  padding-top: 1em;

  li {
    padding-right: 30px;

    &:last-child {
      border-bottom-width: 0;
    }
  }
}

.total-price-block__taxes-excl,
.total-price-block__taxes-incl {
  li {
    display: flex;
    border: 0px solid var(--color-border);
    border-bottom-width: 1px;
    justify-content: space-between;

    span {
      text-align: right;

      &:first-child {
        text-align: left;
      }
    }
  }
}
</style>
